/**
 * TP/SL Edit Modal for Mobile
 * Allows users to edit TP/SL via input fields on mobile devices
 */

import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { StorePosition } from "@/backend/store";

interface TPSLEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  position: StorePosition | null;
  onUpdate: (takeProfit: number | null, stopLoss: number | null) => void;
}

export const TPSLEditModal = ({
  isOpen,
  onClose,
  position,
  onUpdate,
}: TPSLEditModalProps) => {
  const [tpValue, setTpValue] = useState("");
  const [slValue, setSlValue] = useState("");

  // Initialize values when position changes
  useEffect(() => {
    if (position) {
      setTpValue(position.takeProfit ? position.takeProfit.toString() : "");
      setSlValue(position.stopLoss ? position.stopLoss.toString() : "");
    }
  }, [position]);

  if (!position) return null;

  const isLong = position.side === "long";
  const entryPrice = position.entryPrice;

  // Calculate estimated P&L
  const calculatePnL = (price: number) => {
    const pnl = (price - entryPrice) * position.size * (isLong ? 1 : -1);
    const percent = ((price - entryPrice) / entryPrice) * 100 * (isLong ? 1 : -1);
    return { pnl, percent };
  };

  const tpPrice = parseFloat(tpValue) || 0;
  const slPrice = parseFloat(slValue) || 0;
  const tpPnL = tpPrice > 0 ? calculatePnL(tpPrice) : null;
  const slPnL = slPrice > 0 ? calculatePnL(slPrice) : null;

  const handleSubmit = () => {
    const tp = tpValue ? parseFloat(tpValue) : null;
    const sl = slValue ? parseFloat(slValue) : null;
    onUpdate(tp, sl);
    onClose();
  };

  const handleClear = () => {
    setTpValue("");
    setSlValue("");
  };

  const applyPercentageOffset = (isTP: boolean, percent: number) => {
    const offset = entryPrice * (percent / 100);
    const newPrice = isLong
      ? entryPrice + (isTP ? offset : -offset)
      : entryPrice + (isTP ? -offset : offset);

    if (isTP) {
      setTpValue(newPrice.toFixed(2));
    } else {
      setSlValue(newPrice.toFixed(2));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit TP/SL</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Position Info */}
          <div className="p-3 bg-muted rounded-md">
            <div className="text-sm">
              <span className="font-medium">{position.market}</span>
              <span
                className={`ml-2 ${
                  isLong ? "text-bid-foreground" : "text-ask-foreground"
                }`}
              >
                {isLong ? "LONG" : "SHORT"}
              </span>
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Entry: ${entryPrice.toFixed(2)} | Size: {position.size}
            </div>
          </div>

          {/* Take Profit */}
          <div className="space-y-2">
            <Label htmlFor="tp">Take Profit</Label>
            <Input
              id="tp"
              type="number"
              placeholder="Enter TP price"
              value={tpValue}
              onChange={(e) => setTpValue(e.target.value)}
              step="0.01"
            />
            {tpPnL && (
              <div className="text-xs text-muted-foreground">
                Est. P&L: ${tpPnL.pnl.toFixed(2)} ({tpPnL.percent > 0 ? "+" : ""}
                {tpPnL.percent.toFixed(2)}%)
              </div>
            )}
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => applyPercentageOffset(true, 5)}
              >
                +5%
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => applyPercentageOffset(true, 10)}
              >
                +10%
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => applyPercentageOffset(true, 20)}
              >
                +20%
              </Button>
            </div>
          </div>

          {/* Stop Loss */}
          <div className="space-y-2">
            <Label htmlFor="sl">Stop Loss</Label>
            <Input
              id="sl"
              type="number"
              placeholder="Enter SL price"
              value={slValue}
              onChange={(e) => setSlValue(e.target.value)}
              step="0.01"
            />
            {slPnL && (
              <div className="text-xs text-muted-foreground">
                Est. P&L: ${slPnL.pnl.toFixed(2)} ({slPnL.percent > 0 ? "+" : ""}
                {slPnL.percent.toFixed(2)}%)
              </div>
            )}
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => applyPercentageOffset(false, 5)}
              >
                -5%
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => applyPercentageOffset(false, 10)}
              >
                -10%
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => applyPercentageOffset(false, 20)}
              >
                -20%
              </Button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleClear} className="flex-1">
              Clear
            </Button>
            <Button onClick={handleSubmit} className="flex-1">
              Update
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

